/* ==========================================================================
   TT PLACEMENT — SHARED MODULE
   Technician Tees · created 2026-08-31 · DEV theme 194825715995

   THE ONE RULE THIS FILE EXISTS TO ENFORCE
   -----------------------------------------------------------------------
   The visual editor (tt-placecheck) and the customer-facing renderer
   (tt-mockup-preview-v2) MUST compute placement with the SAME code. If the
   maths is duplicated, the two drift, and "what Jeff saved" stops being
   "what the customer sees".

   Nothing in this file reads or writes Shopify. It is pure computation.
   Callers supply the saved metafield object and the artwork URL.

   COORDINATE SYSTEM
   -----------------------------------------------------------------------
   All boxes are percentages of the 2000 x 2200 garment canvas.
     box = { x: centreX%, y: topY%, w: %, h: %, anchor? }
   A box describes the INK rectangle - the visible artwork - not the image
   file. See fitInk().

   THREE-TIER RESOLUTION (Tag & Product Standards 17.15)
     1. saved zone box            -> AUTHORITATIVE, wins outright
     2. physical rule             -> fallback where no override exists
     3. class seed                -> only when a SKU has no garment block
   When a box is authoritative the 3in/4in drops and the 12.5x13in caps are
   NOT applied to it. That is the point of an override, not a bug.

   VISIBLE-INK SIZING
   -----------------------------------------------------------------------
   Artwork is placed by its VISIBLE INK, never by its whole canvas. This
   retires transparent padding as a size lever. Bounds are stored as
   FRACTIONS of the artwork canvas, which is what makes them survive
   Shopify's resize and WebP conversion.

   Verified 2026-08-31 against 10 real client files:
     * Shopify CDN sends access-control-allow-origin:* on originals AND on
       &width= transforms, so crossOrigin='anonymous' does not taint canvas
     * fractional bounds drift <= 0.25% at width=400, <= 0.14% at width=1024
     * alpha threshold 1 vs 128 moves bounds <= 0.10%
     * refetches are byte-identical (max-age=31557600)
     * FORMAT IS NOT PREDICTABLE PER URL - the same request returns PNG for
       one file and WebP for another. Never branch on file extension.
   ========================================================================== */

(function (root) {
  'use strict';

  var CW = 2000, CH = 2200;          // garment canvas
  var COLLAR_Y = 132;                // tee collar landmark

  // Production rules. Global or by class. NOT per SKU.
  var RULES = {
    chestW: 3.5, chestDrop: 3.0, nameH: 0.600,
    maxW: 12.5, maxH: 13.0, backDrop: 4.0,
    hoodDrop: 3.5, youthW: 2.5
  };

  // Body span is per class and the two are NOT comparable. The hooded figure
  // is a represented SHOULDER-to-hem length.
  var BODY_SPAN = { tee: 1936, youth: 1936, hooded: 1500 };

  // Starting seeds only. Not per-SKU, not correct for any given garment.
  var CLASS_SEED = {
    tee:    { body: 29.40, neck: 383, neckBack: 200, off: 3.55 },
    hooded: { body: 27.85, neck: 683, neckBack: 774, off: 3.55 },
    youth:  { body: 24.20, neck: 401, neckBack: 201, off: 2.90 }
  };

  function classOf(handle) {
        if (handle === 'pc78h' || handle === 'f281' || handle === 'j719') return 'hooded';
    if (handle === '55y') return 'youth';
    return 'tee';
  }

  function effective(cls) {
    return {
      chestW:    cls === 'youth'  ? RULES.youthW   : RULES.chestW,
      frontDrop: cls === 'hooded' ? RULES.hoodDrop : RULES.chestDrop,
      backDrop:  RULES.backDrop
    };
  }

  function ppi(cls, body) { return (BODY_SPAN[cls] || BODY_SPAN.tee) / body; }

  /* ---- saved metafield -> garment geometry ------------------------------ */

  function garmentOf(saved, cls) {
    var seed = CLASS_SEED[cls] || CLASS_SEED.tee;
    var g = { body: seed.body, neck: seed.neck, neckBack: seed.neckBack,
              off: seed.off, calibrated: false };
    if (saved && saved.garment) {
      var b = saved.garment;
      if (typeof b.body === 'number') g.body = b.body;
      if (typeof b.neck === 'number') g.neck = b.neck;
      if (typeof b.neckBack === 'number') g.neckBack = b.neckBack;
      if (typeof b.off === 'number') g.off = b.off;
      g.calibrated = true;
    }
    return g;
  }

  function overrideOf(saved, zone) {
    if (!saved) return null;
    if (zone === 'logo_back') return (saved.back && saved.back.logo_back) || null;
    if (zone === 'logo_sleeve') return (saved.sleeve && saved.sleeve.logo_sleeve) || null;
    return (saved.front && saved.front[zone]) || null;
  }

  // EMPLOYEE NAME ON/OFF - added 2026-09-03. Absent means ON, so every record
  // written before today keeps its employee name exactly as before. Only an
  // explicit false disables it. garmentOf() copies numeric keys only, so this
  // flag is ignored harmlessly by any reader that predates it.
  function nameEnabled(saved) {
    return !(saved && saved.garment && saved.garment.name_enabled === false);
  }

  /* ---- box helpers ------------------------------------------------------ */

  function toPx(box) {
    return { cx: box.x / 100 * CW, top: box.y / 100 * CH,
             w: box.w / 100 * CW, h: box.h / 100 * CH };
  }
  function toPct(b, anchor) {
    var o = { x: +(b.cx / CW * 100).toFixed(3), y: +(b.top / CH * 100).toFixed(3),
              w: +(b.w / CW * 100).toFixed(3), h: +(b.h / CH * 100).toFixed(3) };
    if (anchor) o.anchor = anchor;
    return o;
  }

  var ZONE_ANCHOR = {
    logo_chest: 'center', name: null, logo_front_full: 'center',
    logo_back: 'top', logo_sleeve: null
  };

  /* ---- VISIBLE-INK MEASUREMENT ------------------------------------------ */

  var inkCache = {};       // url -> {fx,fy,fw,fh,W,H}
  var inkPending = {};     // url -> Promise
  var MEASURE_WIDTH = 400; // 0.25% accurate, cheap to decode
  var ALPHA_MIN = 8;       // anti-alias tolerant; 1 vs 128 moves bounds <=0.10%

  // Whole-canvas fallback. Used when measurement is impossible. Behaves
  // exactly like the pre-2026-08-31 system, so a failure degrades to the old
  // behaviour rather than to a broken layout.
  var FULL_INK = { fx: 0, fy: 0, fw: 1, fh: 1, measured: false };

  function measureUrl(url) {
    // Ask the CDN for a small variant. Bounds are fractional so scale is
    // irrelevant, and a 400px decode is far cheaper than a 3794px one.
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + MEASURE_WIDTH;
  }

  function measureInk(url) {
    if (!url) return Promise.resolve(FULL_INK);
    if (inkCache[url]) return Promise.resolve(inkCache[url]);
    if (inkPending[url]) return inkPending[url];

    inkPending[url] = new Promise(function (resolve) {
      var img = new Image();
      // REQUIRED for an untainted canvas. Shopify sends ACAO:* so this works.
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        var res;
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0);
          var d = ctx.getImageData(0, 0, w, h).data;
          var minX = w, minY = h, maxX = -1, maxY = -1;
          for (var y = 0; y < h; y++) {
            var row = y * w * 4;
            for (var x = 0; x < w; x++) {
              if (d[row + x * 4 + 3] >= ALPHA_MIN) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0) {
            res = FULL_INK;                       // fully transparent
          } else {
            res = { fx: minX / w, fy: minY / h,
                    fw: (maxX - minX + 1) / w, fh: (maxY - minY + 1) / h,
                    W: w, H: h, measured: true };
          }
        } catch (e) {
          // Tainted canvas or read failure. Degrade to whole-canvas sizing.
          res = FULL_INK;
        }
        inkCache[url] = res;
        resolve(res);
      };
      img.onerror = function () { inkCache[url] = FULL_INK; resolve(FULL_INK); };
      img.src = measureUrl(url);
    });
    return inkPending[url];
  }

  /* ---- fitInk: THE function that makes saved == rendered ----------------- */

  // Given a target INK box in canvas px and an artwork's ink fractions,
  // return where to position the IMAGE ELEMENT so the visible ink lands
  // exactly in that box, aspect ratio preserved, never stretched.
  //
  // The box is the ink rectangle. The image is larger than the box whenever
  // the artwork carries transparent padding - that is correct and is what
  // stops padding acting as a size lever.
  function fitInk(box, ink, anchor) {
    var iw = ink.fw, ih = ink.fh;                 // ink size as canvas fraction
    if (!iw || !ih) { iw = 1; ih = 1; }
    // CORRECTED 2026-08-31. fw/fh are fractions of an artwork canvas that is
    // NOT generally square, so fw/fh is NOT an aspect ratio. Each fraction
    // must be multiplied by its measured canvas dimension to recover the
    // ink's real pixel aspect. The previous form made imgW/imgH identically
    // 1, i.e. a square image rect for every artwork - correct only when the
    // canvas happened to be square, which is why symmetric-padding and
    // square calibration assets hid it. Measured error on real client files:
    // 0.570in (Automotive Magic back) and 4.547in (Oxford back).
    var cw = (ink.W > 0) ? ink.W : 1;             // measured canvas px
    var ch = (ink.H > 0) ? ink.H : 1;
    var arInk = (iw * cw) / (ih * ch);            // true ink aspect
    var boxAr = box.w / box.h;
    var inkW, inkH;
    if (arInk >= boxAr) { inkW = box.w; inkH = box.w / arInk; }
    else                { inkH = box.h; inkW = box.h * arInk; }

    var inkLeft = box.cx - inkW / 2;
    var inkTop  = (anchor === 'top') ? box.top : box.top + (box.h - inkH) / 2;

    // Back out the full image rect from the ink rect.
    var imgW = inkW / iw, imgH = inkH / ih;
    return {
      left:   inkLeft - ink.fx * imgW,
      top:    inkTop  - ink.fy * imgH,
      width:  imgW,
      height: imgH,
      inkLeft: inkLeft, inkTop: inkTop, inkW: inkW, inkH: inkH,
      inkCx: inkLeft + inkW / 2, inkCy: inkTop + inkH / 2
    };
  }

  /* ---- rule boxes (fallbacks only) -------------------------------------- */

  function capBox(ink, p, neck, drop) {
    var arInk = (ink.fw || 1) / (ink.fh || 1);
    var maxW = RULES.maxW * p, maxH = RULES.maxH * p;
    var w, h;
    if (maxW / arInk <= maxH) { w = maxW; h = maxW / arInk; }  // width binds
    else                      { h = maxH; w = maxH * arInk; }  // height binds
    return { cx: CW / 2, top: neck + drop * p, w: w, h: h };
  }

  function ruleBox(zone, ink, g, cls) {
    var p = ppi(cls, g.body), eff = effective(cls);
    var arInk = (ink.fw || 1) / (ink.fh || 1);
    if (zone === 'logo_chest') {
      var w = eff.chestW * p;
      return { cx: CW / 2 + g.off * p, top: g.neck + eff.frontDrop * p,
               w: w, h: w / arInk };
    }
    if (zone === 'name') {
      var c = ruleBox('logo_chest', ink, g, cls);
      var nh = RULES.nameH * p;
      return { cx: CW / 2 - g.off * p, top: c.top + c.h / 2 - nh / 2,
               w: nh * 3, h: nh };
    }
    if (zone === 'logo_sleeve') {
      // No documented sleeve production rule. Starting position only.
      var sw = eff.chestW * p;
      return { cx: 0.38 * CW, top: 0.37 * CH, w: sw, h: sw / arInk };
    }
    if (zone === 'logo_front_full') return capBox(ink, p, g.neck, eff.frontDrop);
    return capBox(ink, p, g.neckBack, eff.backDrop);
  }

  /* ---- resolve: the single entry point both callers use ------------------ */

  // saved  : parsed mock.placement_json_v2 (or null)
  // zone   : logo_chest | name | logo_front_full | logo_back | logo_sleeve
  // ink    : result of measureInk() for THIS client's artwork
  // handle : product handle, for class
  //
  // Returns { box, source, anchor } with box in canvas px.
  function resolve(zone, saved, ink, handle) {
    var cls = classOf(handle);
    var g = garmentOf(saved, cls);
    var ov = overrideOf(saved, zone);
    if (ov) {
      return { box: toPx(ov), source: 'override',
               anchor: ov.anchor || ZONE_ANCHOR[zone], garment: g, cls: cls };
    }
    return { box: ruleBox(zone, ink, g, cls), source: 'rule',
             anchor: ZONE_ANCHOR[zone], garment: g, cls: cls };
  }

  // Convenience: resolve + fitInk in one call. This is what a renderer wants.
  function place(zone, saved, ink, handle) {
    var r = resolve(zone, saved, ink, handle);
    var fit = fitInk(r.box, ink, r.anchor);
    fit.source = r.source; fit.garment = r.garment; fit.cls = r.cls;
    fit.box = r.box; fit.anchor = r.anchor;
    return fit;
  }

  /* ---- employee name ----------------------------------------------------- */

  // Name w/h are DERIVED, never authored. A saved name box supplies POSITION
  // only; its dimensions are recomputed from the 0.600in rule and current
  // scale. Callers must recompute at write time too - never persist editor
  // state, or a later body-length change leaves stale dimensions stored.
  function nameGeometry(saved, handle, textMetrics) {
    var cls = classOf(handle);
    var g = garmentOf(saved, cls);
    var p = ppi(cls, g.body);
    var h = RULES.nameH * p;                       // ALWAYS 0.600in
    var w = textMetrics ? (textMetrics.w / textMetrics.h) * h : h * 3;
    var ov = overrideOf(saved, 'name');
    var cx, cy;
    if (ov) {
      var b = toPx(ov);
      cx = b.cx; cy = b.top + b.h / 2;             // centre is preserved exactly
    } else {
      cx = CW / 2 - g.off * p;
      cy = null;                                   // caller puts it on the laser
    }
    return { cx: cx, cy: cy, w: w, h: h, ppi: p, garment: g, cls: cls,
             enabled: nameEnabled(saved) };
  }

  root.TTPlacement = {
    CW: CW, CH: CH, COLLAR_Y: COLLAR_Y,
    RULES: RULES, BODY_SPAN: BODY_SPAN, CLASS_SEED: CLASS_SEED,
    ZONE_ANCHOR: ZONE_ANCHOR, FULL_INK: FULL_INK,
    classOf: classOf, effective: effective, ppi: ppi,
    garmentOf: garmentOf, overrideOf: overrideOf, nameEnabled: nameEnabled,
    toPx: toPx, toPct: toPct,
    measureInk: measureInk, fitInk: fitInk,
    ruleBox: ruleBox, resolve: resolve, place: place,
    nameGeometry: nameGeometry,
    _inkCache: inkCache
  };
})(window);
